import { OptionsMenuStorybook } from "@finanedi.es/storybook"
import {
  faArrowDownToBracket,
  faCoins,
  faTrashCan,
} from "@fortawesome/pro-light-svg-icons"
import { FacturaReadingFiltersDtoProps } from "api/DTOs/FacturaReadingFiltersDto"
import { ESTADO_REVISION_DOCUMENTO_COMERCIAL } from "api/Types/EstadoRevisionDocumentoComercial"
import { ESTADOS_PAGO } from "api/Types/EstadosPago"
import { formatCurrency } from "common/utils/currency"
import { EstadoRevisionActual } from "features/Documentos/components/EstadoRevisionActual/EstadoRevisionActual"
import { ModalTypeDownloadDocumentProps } from "features/Documentos/components/ModalTypeDownloadDocument/constant"
import { TFunction } from "i18next"
import { frontendDateFormat } from "utils/formatDate"
import { EstadoCobroPagoFactura } from "../../FacturasEnviadas/components/EstadoCobroPago"
import { EstadoOrigenFactura } from "../../FacturasEnviadas/components/EstadoOrgien"

interface ColumnasFacturasRecibidasParams {
  t: TFunction
  setShowModalDownload: React.Dispatch<
    React.SetStateAction<ModalTypeDownloadDocumentProps["modalState"]>
  >
  setShowModalPagoCobroManual: React.Dispatch<
    React.SetStateAction<{
      show: boolean
      id: string | undefined
      serie: string | null | undefined
    }>
  >
  showContacto?: boolean
  toggleDeleteModal?: (id?: string) => void
}

export const COLUMNAS_FACTURAS_RECIBIDAS_ERP = ({
  t,
  setShowModalDownload,
  setShowModalPagoCobroManual,
  showContacto = true,
  toggleDeleteModal = () => undefined,
}: ColumnasFacturasRecibidasParams) => {
  const columns = [
    {
      header: t("crear.factura.tab.headerEmisionDate"),
      field: "fechaEmision",
      key: "fechaEmision",
      body: ({ fechaEmision }: FacturaReadingFiltersDtoProps) =>
        fechaEmision ? frontendDateFormat(new Date(fechaEmision)) : "",
      style: { width: "10%" },
      sortable: true,
      sortField: "fechaEmision",
    },
    {
      header: t("crear.factura.tab.headerNumOrder"),
      field: "serie",
      key: "serie",
      style: { width: "15%" },
      sortable: true,
      sortField: "serie",
    },
    showContacto
      ? {
          header: t("crear.factura.tab.headerProveedor"),
          key: "cliente",
          field: "Librador.RazonSocial",
          body: (rowData: FacturaReadingFiltersDtoProps) => {
            return `${rowData.razonSocialLibrador} (${rowData.nifLibrador})`
          },
          sortable: true,
          sortField: "Librador.RazonSocial",
        }
      : {},
    {
      header: t("crear.factura.tab.headerState"),
      field: "estadoRevisionActual",
      key: "estadoRevisionActual",
      body: ({ estadoRevisionActual }: FacturaReadingFiltersDtoProps) => {
        return estadoRevisionActual
          ? EstadoRevisionActual(estadoRevisionActual)
          : ""
      },
      style: { width: "11%" },
      sortable: true,
      alignCol: "center",
      sortField: "estadoRevisionActual",
    },
    {
      header: t("crear.factura.tab.headerImport"),
      field: "importe",
      key: "importe",
      bodyClassName: "!tw-text-end",
      alignCol: "right",
      style: { width: "11%" },
      body: ({ importe }: FacturaReadingFiltersDtoProps) =>
        formatCurrency(importe),
      sortable: true,
      sortField: "importe",
    },
    {
      header: t("crear.factura.tab.headerStatusPayPago"),
      key: "estadoPago",
      field: "EstadoPagoFactura.EstadoPago",
      style: { width: "200px" },
      body: (rowData: FacturaReadingFiltersDtoProps) =>
        EstadoCobroPagoFactura({ rowData, isVenta: false, t }),
      sortable: true,
      bodyClassName: "!tw-text-end",
      alignCol: "right",
      sortField: "EstadoPagoFactura.EstadoPago",
    },
    {
      header: t("crear.factura.tab.headerOrigin"),
      key: "origen",
      field: "origen",
      style: { width: "5%" },
      body: (rowData: FacturaReadingFiltersDtoProps) =>
        EstadoOrigenFactura({ origen: rowData.origen, id: rowData.id, t }),
      alignCol: "center",
      bodyClassName: "!tw-text-center",
    },
    {
      key: "opciones",
      style: { width: "4%" },
      className: "!tw-p-2",
      bodyClassName: "!tw-w-auto tw-flex tw-justify-end",
      alignCol: "right",
      body: (rowData: FacturaReadingFiltersDtoProps) => {
        if (!rowData.id) return null

        return (
          <OptionsMenuStorybook
            id={rowData.id}
            items={[
              {
                title: `${t("crear.factura.tab.downloadLabel")}`,
                hasRedirect: true,
                action: () => {
                  setShowModalDownload({
                    show: true,
                    id: rowData.id,
                    serie: `${rowData.serie}`,
                  })
                },
                icon: faArrowDownToBracket,
              },
              {
                title: `${t("crear.factura.tab.markPaid")}`,
                hasRedirect: true,
                action: () =>
                  setShowModalPagoCobroManual({
                    show: true,
                    id: rowData?.id,
                    serie: rowData?.serie,
                  }),

                show:
                  rowData.estadoPago !== ESTADOS_PAGO.PAGADA &&
                  rowData.estadoRevisionActual ===
                    ESTADO_REVISION_DOCUMENTO_COMERCIAL.VALIDADO,
                icon: faCoins,
              },
              ...(rowData?.puedeBorrarse
                ? [
                    {
                      title: `${t("nsAssets.detailHeader.menu.delete")}`,
                      hasRedirect: false,
                      action: () => toggleDeleteModal(rowData.id),
                      icon: faTrashCan,
                      className: "tw-text-danger",
                      iconClassName: "tw-text-danger",
                    },
                  ]
                : []),
            ]}
            position='right'
          />
        )
      },
    },
  ]
  return columns
}
